import React, { createRef, useState } from 'react';
import { Form, FormItem, SubmitButton } from 'formik-antd';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { Button, Col, Row, Upload, Typography, Slider, Modal } from 'antd';
import { RiUploadCloud2Line } from 'react-icons/ri';
import { useIntl } from 'react-intl';
import AvatarEditor from 'react-avatar-editor';
import Yup from '../../../../vendor/yup';
import { FILE_SIZE, SUPPORTED_FORMATS, SUPPORTED_TYPES } from './FormUploadAvatar.const';
import IntlMessages from '../../../../layout/components/lang/IntlMessages';
import Spinner from '../../../../components/Spinner';



const { Text } = Typography;

const validationSchema = Yup.object().shape({
  list: Yup.mixed()
    .required('A file is required')
    .test(
      'fileSize',
      'File size is too large and should be less than 400Kb',
      (value) => {
        if (value.length) {
          return value && value[0]?.originFileObj?.size <= FILE_SIZE;
        }
        return true;
      },
    ),
});

const FormUploadAvatar = ({ onSubmit, onCancel, isSubmitting }) => {
  const intl = useIntl();
  const editorRef = createRef();
  const [ image, setImage ] = useState(null);
  const [ scale, setScale ] = useState(1);
  const [ showEditor, setShowEditor ] = useState(false);

  const handleSave = async (setFieldValue) => {
    if (editorRef.current) {
      const canvas = editorRef.current.getImageScaledToCanvas();
      const croppedImage = canvas.toDataURL();

      // Конвертируем base64 в File объект
      const fetchRes = await fetch(croppedImage);
      const blob = await fetchRes.blob();
      const file = new File([ blob ], 'avatar.png', { type: 'image/png' });

      setFieldValue('avatar', file);
      setFieldValue('list', [
        {
          originFileObj: file,
          name: 'avatar.png',
          status: 'done',
          url: croppedImage,
        },
      ]);
      setShowEditor(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      validationSchema={validationSchema}
      isSubmitting={isSubmitting}
      initialValues={{
        avatar: null,
        list: [],
      }}
      onSubmit={async (values, { resetForm, setErrors }) => {
        await onSubmit(values);
        resetForm();
        setErrors({});
      }}
    >
      {({ values, errors, isValid, dirty, setFieldValue, setErrors, resetForm }) => (
        <Form layout="vertical" className='w-full'>
          <Spinner spinning={isSubmitting}>
            <Modal
              visible={showEditor}
              title={<IntlMessages id='form-upload-user-avatar-edit-title' />}
              onCancel={() => setShowEditor(false)}
              onOk={() => handleSave(setFieldValue)}
              okText={<IntlMessages id='ui-general-ok' />}
              cancelText={<IntlMessages id='ui-general-cancel' />}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                <AvatarEditor
                  ref={editorRef}
                  image={image}
                  width={250}
                  height={250}
                  border={50}
                  scale={scale}
                  borderRadius={125}
                  color={[ 0, 0, 0, 0.6 ]}
                />
                <div style={{ width: '100%' }}>
                  <p><IntlMessages id='ui-general-scale' /></p>
                  <Slider
                    min={1}
                    max={3}
                    step={0.1}
                    value={scale}
                    onChange={(value) => setScale(value)}
                  />
                </div>
              </div>
            </Modal>

            <FormItem
              label={intl.formatMessage({ id: 'form-upload-user-avatar-label' })}
              name='avatar'
              valuePropName="avatar"
              required
            >
              <Upload.Dragger
                name="avatar"
                accept={SUPPORTED_FORMATS}
                type={SUPPORTED_TYPES}
                multiple={false}
                maxCount={1}
                listType="picture"
                disabled={isSubmitting}
                fileList={values.list}
                beforeUpload={(file) => {
                  const reader = new FileReader();

                  reader.onload = () => {
                    setImage(reader.result);
                    setShowEditor(true);
                  };
                  reader.readAsDataURL(file);
                  return false;
                }}
                onRemove={async () => {
                  await setFieldValue('list', []);
                  await setFieldValue('avatar', null);
                  setErrors({});
                  setImage(null);
                }}
              >
                <p className="ant-upload-drag-icon">
                  <RiUploadCloud2Line className="remix-icon" />
                </p>
                <p className="ant-upload-text">
                  <IntlMessages id="form-upload-user-avatar-description" />
                </p>
              </Upload.Dragger>
              <div className="hp-mt-4">
                <Text type="danger">{errors.list ? errors.list : null}</Text>
              </div>
            </FormItem>

            <Row gutter={[ 16, 16 ]} justify='center'>
              <Col>
                <Button
                  type='secondary'
                  onClick={async () => {
                    onCancel();
                    await setFieldValue('avatar', null);
                    await setFieldValue('list', []);
                    resetForm();
                    setErrors({});
                    setImage(null);
                  }}
                >
                  <IntlMessages id="ui-general-cancel" />
                </Button>
              </Col>
              <Col>
                <SubmitButton
                  loading={isSubmitting}
                  disabled={!isValid || !dirty}
                >
                  <IntlMessages id="ui-general-submit" />
                </SubmitButton>
              </Col>
            </Row>
          </Spinner>
        </Form>
      )}
    </Formik>
  );
};

FormUploadAvatar.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};

export default FormUploadAvatar;
